/**
 * Vehicle Finder - Donicars (FULL)
 * - Mode réel via JSON d'assets (vf_brands.json + vf_brand_XXX.json) + prix déjà dedans
 * - Hydratation images étape 6 via /products/{handle}.js (featured_image)
 * - FIX PRIX: parsing robuste (virgules, symboles, objets) + fallback live via /products/{handle}.js si prix non lisible
 *
 * window.vfConfig attendu :
 * {
 *   useRealData: true,
 *   debug: true,
 *   brandsUrl: "https://.../assets/vf_brands.json",
 *   assetsBase: "https://.../assets/",
 * }
 */

class VehicleFinder {
  constructor() {
    this.state = {
      currentStep: 1,
      selection: {
        brand: null,
        model: null,
        type: null,
        year: null,
        technology: null
      },
      data: null,
      useRealData: false,
      debug: true
    };

    // cache product.js (handle -> { imageUrl, price, comparePrice } | null)
    this.productCache = new Map();

    this.init();
  }

  // ------------------- INIT -------------------

  async init() {
    const cfg = window.vfConfig || {};
    this.state.useRealData = !!cfg.useRealData;
    this.state.debug = cfg.debug !== undefined ? !!cfg.debug : true;

    await this.loadData();
    if (!this.state.data) this.loadDemoData();

    this.renderStep();
    this.setupEventListeners();

    if (this.state.debug) {
      console.log("[VF] init", {
        useRealData: this.state.useRealData,
        brands: this.state.data?.brands?.length,
        brandsUrl: cfg.brandsUrl,
        assetsBase: cfg.assetsBase
      });
    }
  }

  async loadData() {
    if (this.state.useRealData) {
      const ok = await this.loadRealDataFromAssets();
      if (!ok) this.loadDemoData();
      return;
    }
    this.loadDemoData();
  }

  // ------------------- REAL DATA (ASSETS JSON) -------------------

  async loadRealDataFromAssets() {
    const cfg = window.vfConfig || {};
    const brandsUrl = cfg.brandsUrl;
    const assetsBase = cfg.assetsBase;

    if (!brandsUrl || !assetsBase) {
      if (this.state.debug) console.warn("[VF] Missing vfConfig.brandsUrl/assetsBase");
      return false;
    }

    try {
      const brandsJson = await fetch(brandsUrl, { credentials: "same-origin" }).then((r) => r.json());
      const brands = Array.isArray(brandsJson?.brands) ? brandsJson.brands : [];

      if (!brands.length) {
        if (this.state.debug) console.warn("[VF] brands.json empty");
        return false;
      }

      const acc = {
        brands: [],
        models: {},        // brand -> [models]
        types: {},         // brand|model -> [types]
        years: {},         // brand|model|type -> [years]
        technologies: {},  // brand|model|type|year -> [tech]
        packs: {}          // brand|model|type|year|tech -> [packs]
      };

      const setAdd = (obj, key, val) => {
        if (!obj[key]) obj[key] = new Set();
        obj[key].add(val);
      };

      const ensureArr = (setOrArr) =>
        Array.from(setOrArr || []).sort((a, b) => String(a).localeCompare(String(b)));

      for (const brand of brands) {
        const safe = String(brand || "").toUpperCase().replace(/[^\w\-]+/g, "_");
        const brandUrl = assetsBase + `vf_brand_${safe}.json`;

        let brandData = null;
        try {
          const r = await fetch(brandUrl, { credentials: "same-origin" });
          if (!r.ok) {
            if (this.state.debug) console.warn("[VF] brand json fetch failed", brandUrl, r.status);
            continue;
          }
          brandData = await r.json();
        } catch (e) {
          if (this.state.debug) console.warn("[VF] brand json parse failed", brandUrl, e);
          continue;
        }

        const modelsObj = brandData?.models || {};
        const modelNames = Object.keys(modelsObj);

        acc.brands.push(brand);
        acc.models[brand] = modelNames.sort((a, b) => a.localeCompare(b));

        for (const model of modelNames) {
          const typesObj = modelsObj[model] || {};
          const typeNames = Object.keys(typesObj);

          const modelKey = `${brand}|${model}`;
          typeNames.forEach((ty) => setAdd(acc.types, modelKey, ty));

          for (const type of typeNames) {
            const yearsObj = typesObj[type] || {};
            const yearNames = Object.keys(yearsObj);

            const typeKey = `${brand}|${model}|${type}`;
            yearNames.forEach((y) => setAdd(acc.years, typeKey, y));

            for (const year of yearNames) {
              const techObj = yearsObj[year] || {};
              const techNames = Object.keys(techObj);

              const yearKey = `${brand}|${model}|${type}|${year}`;
              techNames.forEach((te) => setAdd(acc.technologies, yearKey, te));

              for (const tech of techNames) {
                const packsArr = Array.isArray(techObj[tech]) ? techObj[tech] : [];
                const packsKey = `${brand}|${model}|${type}|${year}|${tech}`;
                if (!acc.packs[packsKey]) acc.packs[packsKey] = [];
                packsArr.forEach((p) => acc.packs[packsKey].push(p));
              }
            }
          }
        }
      }

      acc.brands = acc.brands.sort((a, b) => a.localeCompare(b));
      acc.types = Object.fromEntries(Object.entries(acc.types).map(([k, v]) => [k, ensureArr(v)]));
      acc.years = Object.fromEntries(Object.entries(acc.years).map(([k, v]) => [k, ensureArr(v)]));
      acc.technologies = Object.fromEntries(Object.entries(acc.technologies).map(([k, v]) => [k, ensureArr(v)]));

      this.state.data = acc;

      if (this.state.debug) console.log("[VF] real assets data built", { brands: acc.brands.length });
      return acc.brands.length > 0;
    } catch (e) {
      console.error("[VF] loadRealDataFromAssets exception", e);
      return false;
    }
  }

  // ------------------- DEMO -------------------

  loadDemoData() {
    this.state.data = {
      brands: [
        "AUDI","BMW","MERCEDES","VOLKSWAGEN","RENAULT",
        "PEUGEOT","CITROËN","FORD","OPEL","NISSAN",
        "TOYOTA","HONDA","HYUNDAI","KIA","SEAT",
        "SKODA","MAZDA","FIAT","ALFA ROMEO","JEEP"
      ],
      models: {
        "AUDI": ["A1","A3","A4","A5","A6","A7","A8","Q2","Q3","Q5","Q7","Q8","TT","R8"],
        "BMW": ["Serie 1","Serie 2","Serie 3","Serie 4","Serie 5","Serie 6","Serie 7","X1","X2","X3","X4","X5","X6","Z4"],
        "MERCEDES": ["Classe A","Classe B","Classe C","Classe E","Classe S","GLA","GLB","GLC","GLE","GLS","CLA","CLS"],
        "VOLKSWAGEN": ["Golf","Polo","Passat","Tiguan","Touran","T-Roc","Arteon","ID.3","ID.4","T-Cross"],
        "RENAULT": ["Clio","Megane","Captur","Kadjar","Scenic","Twingo","Zoe","Arkana","Austral"],
        "PEUGEOT": ["208","308","2008","3008","5008","Partner","Rifter","508"],
        "CITROËN": ["C3","C4","C5","Berlingo","SpaceTourer","C3 Aircross","C5 Aircross"],
        "FORD": ["Fiesta","Focus","Kuga","Puma","Mustang","Mondeo","S-Max"],
        "OPEL": ["Corsa","Astra","Insignia","Crossland","Grandland","Mokka"],
        "NISSAN": ["Micra","Juke","Qashqai","X-Trail","Leaf","Navara"]
      },
      types: {},
      years: {},
      technologies: {},
      packs: {}
    };

    Object.keys(this.state.data.models).forEach((brand) => {
      this.state.data.models[brand].forEach((model) => {
        const key = `${brand}|${model}`;
        this.state.data.types[key] = this.generateTypes();
      });
    });
  }

  generateTypes() {
    const engines = ["1.0 TSI","1.2 TSI","1.4 TSI","1.5 TSI","2.0 TSI","1.6 TDI","2.0 TDI","3.0 TDI"];
    return engines.slice(0, Math.floor(Math.random() * 5) + 3);
  }

  generateYears() {
    return ["2010 à 2013","2013 à 2016","2016 à 2020","2020 à 2023"];
  }

  generateTechnologies() {
    return ["Standard","With Xenon headlights","With LED headlights"];
  }

  generateDemoPacks() {
    return [
      {
        pack_type: "Complet (Intérieur + Extérieur)",
        price: 189.9,
        compare_price: 209.9,
        functions:
          "Feux de croisement, Feux de route, Antibrouillard avant, Feu de recul, Feu antibrouillard arrière, Plafonnier"
      },
      {
        pack_type: "Extérieur",
        price: 129.9,
        compare_price: 149.9,
        functions: "Feux de croisement, Feux de route, Antibrouillard avant"
      },
      {
        pack_type: "Feux avant",
        price: 89.9,
        compare_price: 99.9,
        functions: "Feux de croisement, Feux de route"
      }
    ];
  }

  // ------------------- PRODUCT.JS HYDRATION (IMAGES + PRICE FALLBACK) -------------------

  async fetchProductInfo(handle) {
    if (!handle) return null;
    if (this.productCache.has(handle)) return this.productCache.get(handle);

    try {
      const p = await fetch(`/products/${handle}.js`, { credentials: "same-origin" }).then((r) => r.json());

      const imageUrl =
        p?.featured_image ||
        (Array.isArray(p?.images) ? p.images[0] : null) ||
        null;

      // Shopify product.js: prices are generally in cents (integers).
      // On prend la 1ère variante.
      const v = p?.variants?.[0] || null;
      const centsPrice = this.toCents(v?.price);
      const centsCompare = this.toCents(v?.compare_at_price);

      const price = centsPrice != null ? centsPrice / 100 : null;
      const comparePrice = centsCompare != null ? centsCompare / 100 : null;

      const info = { imageUrl, price, comparePrice };
      this.productCache.set(handle, info);
      return info;
    } catch {
      this.productCache.set(handle, null);
      return null;
    }
  }

  async hydrateResultCards(products) {
    const handles = Array.from(new Set((products || []).map((p) => p.handle).filter(Boolean)));

    for (const handle of handles) {
      const info = await this.fetchProductInfo(handle);
      if (!info) continue;

      // image
      if (info.imageUrl) {
        const imgSel = `img.vf-product-img[data-img-handle="${this.cssEscape(handle)}"]`;
        document.querySelectorAll(imgSel).forEach((img) => {
          img.src = info.imageUrl;
          img.alt = handle;
          img.classList.remove("hidden");
          const ph = img.parentElement?.querySelector(".vf-product-image-placeholder");
          if (ph) ph.classList.add("hidden");
        });
      }

      // price fallback (si l'UI affiche encore "Voir le prix" / chargement)
      const priceSel = `[data-price-handle="${this.cssEscape(handle)}"]`;
      document.querySelectorAll(priceSel).forEach((el) => {
        const current = info.price;
        if (current == null) return;
        el.textContent = `${Number(current).toFixed(2)}€`;
        el.classList.remove("vf-price-loading");
      });

      const compareSel = `[data-compare-handle="${this.cssEscape(handle)}"]`;
      document.querySelectorAll(compareSel).forEach((el) => {
        const compare = info.comparePrice;
        if (compare == null) return;
        el.textContent = `${Number(compare).toFixed(2)}€`;
        el.classList.remove("hidden");
      });

      const saveSel = `[data-save-handle="${this.cssEscape(handle)}"]`;
      document.querySelectorAll(saveSel).forEach((el) => {
        const current = info.price;
        const compare = info.comparePrice;
        if (current == null || compare == null || compare <= current) return;
        const savings = (((compare - current) / compare) * 100).toFixed(0);
        el.textContent = `-${savings}%`;
        el.classList.remove("hidden");
      });
    }
  }

  cssEscape(s) {
    if (window.CSS && typeof window.CSS.escape === "function") return window.CSS.escape(String(s));
    return String(s).replace(/["\\]/g, "\\$&");
  }

  // ------------------- UI -------------------

  renderStep() {
    this.updateProgress();

    const titles = {
      1: "Sélectionnez votre marque",
      2: "Sélectionnez le modèle",
      3: "Sélectionnez le type de motorisation",
      4: "Sélectionnez l'année",
      5: "Sélectionnez la technologie des phares",
      6: "Résultats"
    };

    const subtitles = {
      1: "Choisissez la marque de votre véhicule pour commencer",
      2: `Modèles disponibles pour ${this.state.selection.brand}`,
      3: `Types de motorisation pour ${this.state.selection.brand} ${this.state.selection.model}`,
      4: "Années de production disponibles",
      5: "Type de phares de votre véhicule",
      6: "Produits compatibles avec votre véhicule"
    };

    const titleEl = document.getElementById("vfSectionTitle");
    const subEl = document.getElementById("vfSectionSubtitle");
    if (titleEl) titleEl.textContent = titles[this.state.currentStep];
    if (subEl) subEl.textContent = subtitles[this.state.currentStep];

    const searchBox = document.getElementById("vfSearchBox");
    if (searchBox) searchBox.classList.toggle("hidden", this.state.currentStep > 2);

    if (this.state.currentStep <= 2) {
      const placeholder = this.state.currentStep === 1 ? "Rechercher une marque..." : "Rechercher un modèle...";
      const input = document.getElementById("vfSearchInput");
      if (input) {
        input.placeholder = placeholder;
        input.value = "";
      }
    }

    const backBtn = document.getElementById("vfBtnBack");
    if (backBtn) backBtn.style.display = this.state.currentStep > 1 ? "block" : "none";

    this.renderOptions();
  }

  renderOptions() {
    const container = document.getElementById("vfOptionsContainer");
    if (!container) return;

    let options = [];
    let useGrid = true;

    switch (this.state.currentStep) {
      case 1:
        options = this.state.data.brands || [];
        break;
      case 2:
        options = this.state.data.models?.[this.state.selection.brand] || [];
        break;
      case 3: {
        const modelKey = `${this.state.selection.brand}|${this.state.selection.model}`;
        options = this.state.data.types?.[modelKey] || this.generateTypes();
        useGrid = false;
        break;
      }
      case 4: {
        const typeKey = `${this.state.selection.brand}|${this.state.selection.model}|${this.state.selection.type}`;
        options = this.state.data.years?.[typeKey] || this.generateYears();
        useGrid = false;
        break;
      }
      case 5: {
        const yearKey = `${this.state.selection.brand}|${this.state.selection.model}|${this.state.selection.type}|${this.state.selection.year}`;
        options = this.state.data.technologies?.[yearKey] || this.generateTechnologies();
        useGrid = false;
        break;
      }
      case 6:
        this.showResults();
        return;
    }

    if (!Array.isArray(options)) options = [];

    if (useGrid) {
      container.innerHTML = `<div class="vf-options-grid">${
        options
          .map(
            (opt) => `
          <div class="vf-option-card" data-value="${this.escapeHtml(opt)}">
            ${this.escapeHtml(opt)}
          </div>
        `
          )
          .join("")
      }</div>`;
    } else {
      container.innerHTML = `<div class="vf-options-list">${
        options
          .map(
            (opt) => `
          <div class="vf-option-item" data-value="${this.escapeHtml(opt)}">
            <span>${this.escapeHtml(opt)}</span>
            <span class="vf-option-item-icon">→</span>
          </div>
        `
          )
          .join("")
      }</div>`;
    }

    container.querySelectorAll(".vf-option-card, .vf-option-item").forEach((el) => {
      el.addEventListener("click", (e) => this.selectOption(el.dataset.value, e));
    });
  }

  selectOption(value, event) {
    const stepKeys = ["brand", "model", "type", "year", "technology"];
    const key = stepKeys[this.state.currentStep - 1];
    this.state.selection[key] = value;

    document.querySelectorAll(".vf-option-card, .vf-option-item").forEach((el) => el.classList.remove("selected"));

    const clicked = event.target.closest(".vf-option-card, .vf-option-item");
    if (clicked) clicked.classList.add("selected");

    const nextBtn = document.getElementById("vfBtnNext");
    if (nextBtn) nextBtn.disabled = false;

    setTimeout(() => {
      if (this.state.currentStep < 6) {
        this.state.currentStep++;
        if (nextBtn) nextBtn.disabled = true;
        this.renderStep();
      }
    }, 200);
  }

  updateProgress() {
    const progress = ((this.state.currentStep - 1) / 5) * 100;
    const fill = document.getElementById("vfProgressFill");
    if (fill) fill.style.width = `${progress}%`;

    for (let i = 1; i <= 6; i++) {
      const circle = document.getElementById(`vfStep${i}`);
      const label = circle?.nextElementSibling;
      if (!circle) continue;

      circle.classList.remove("active", "completed");
      if (label) label.classList.remove("active");

      if (i < this.state.currentStep) {
        circle.classList.add("completed");
        circle.textContent = "✓";
      } else if (i === this.state.currentStep) {
        circle.classList.add("active");
        if (label) label.classList.add("active");
        circle.textContent = i;
      } else {
        circle.textContent = i;
      }
    }
  }

  showResults() {
    const sel = document.getElementById("vfSelectionSection");
    const res = document.getElementById("vfResultsSection");
    if (sel) sel.style.display = "none";
    if (res) res.classList.add("active");

    const vehicleInfo =
      `${this.state.selection.brand} ${this.state.selection.model} ${this.state.selection.type} ` +
      `(${this.state.selection.year}) - ${this.state.selection.technology}`;

    const infoEl = document.getElementById("vfVehicleInfo");
    if (infoEl) infoEl.textContent = vehicleInfo;

    const key =
      `${this.state.selection.brand}|${this.state.selection.model}|${this.state.selection.type}|` +
      `${this.state.selection.year}|${this.state.selection.technology}`;

    const products = this.state.data.packs?.[key] || this.generateDemoPacks();

    // Debug ultra utile: tu vois si tes prix sont des strings "84,90" etc.
    if (this.state.debug) {
      console.log("[VF] results products sample", (products || []).slice(0, 3));
    }

    const container = document.getElementById("vfProductsContainer");
    if (!container) return;

    container.innerHTML = (products || [])
      .map((product) => {
        const handle = product.handle || "";

        // Parsing robuste: accepte number, "84.90", "84,90", "84,90 €", {amount:"84.90"}, etc.
        const price = this.toMoney(product.price);
        const comparePrice = this.toMoney(product.compare_price);

        const current = price ?? comparePrice;
        const original =
          price != null && comparePrice != null && comparePrice > price ? comparePrice : null;

        const savings =
          original != null && current != null
            ? (((original - current) / original) * 100).toFixed(0)
            : null;

        // Si pas de prix lisible -> on affichera un placeholder et on tentera un fallback via product.js
        const priceHtml =
          current != null
            ? `${Number(current).toFixed(2)}€`
            : `<span class="vf-price-loading" data-price-handle="${this.escapeHtml(handle)}">Chargement…</span>`;

        const originalHtml =
          original != null
            ? `
              <span class="vf-price-original" data-compare-handle="${this.escapeHtml(handle)}">${original.toFixed(2)}€</span>
              <span class="vf-price-save" data-save-handle="${this.escapeHtml(handle)}">-${savings}%</span>
            `
            : `
              <span class="vf-price-original hidden" data-compare-handle="${this.escapeHtml(handle)}"></span>
              <span class="vf-price-save hidden" data-save-handle="${this.escapeHtml(handle)}"></span>
            `;

        return `
        <div class="vf-product-card">
          <div class="vf-product-image">
            <div class="vf-product-image-placeholder">💡</div>
            <img class="vf-product-img hidden" data-img-handle="${this.escapeHtml(handle)}" alt="">
          </div>

          <div class="vf-product-info">
            <span class="vf-product-badge">${this.escapeHtml(product.pack_type || "Pack")}</span>
            <h3 class="vf-product-title">
              ${this.escapeHtml(product.title || `Pack LED ${this.state.selection.brand} ${this.state.selection.model}`)}
            </h3>

            <ul class="vf-product-features">
              ${(product.functions || "")
                .split(",")
                .filter(Boolean)
                .map((f) => `<li>${this.escapeHtml(f.trim())}</li>`)
                .join("")}
              <li>Température 6500K Blanc Pur</li>
              <li>Garantie 2 ans</li>
              <li>Compatible CanBus</li>
            </ul>

            <div class="vf-product-price">
              <span class="vf-price-current">${priceHtml}</span>
              ${originalHtml}
            </div>

            <div class="vf-product-actions">
              <button class="vf-btn-add-cart" data-handle="${this.escapeHtml(handle)}">
                🛒 Ajouter au panier
              </button>
              <button class="vf-btn-view-details" data-handle="${this.escapeHtml(handle)}">
                Voir les détails
              </button>
            </div>
          </div>
        </div>
      `;
      })
      .join("");

    container.querySelectorAll(".vf-btn-add-cart").forEach((btn) => {
      btn.addEventListener("click", () => this.addToCart(btn.dataset.handle));
    });

    container.querySelectorAll(".vf-btn-view-details").forEach((btn) => {
      btn.addEventListener("click", () => this.viewDetails(btn.dataset.handle));
    });

    // Hydrate after paint: images + price fallback si besoin
    setTimeout(() => this.hydrateResultCards(products), 0);
  }

  setupEventListeners() {
    const nextBtn = document.getElementById("vfBtnNext");
    const backBtn = document.getElementById("vfBtnBack");
    const backToSearchBtn = document.getElementById("vfBtnBackToSearch");
    const searchInput = document.getElementById("vfSearchInput");

    if (nextBtn) {
      nextBtn.addEventListener("click", () => {
        if (this.state.currentStep < 6) {
          this.state.currentStep++;
          nextBtn.disabled = true;
          this.renderStep();
        }
      });
    }

    if (backBtn) {
      backBtn.addEventListener("click", () => {
        if (this.state.currentStep > 1) {
          this.state.currentStep--;
          const stepKeys = ["brand", "model", "type", "year", "technology"];
          this.state.selection[stepKeys[this.state.currentStep - 1]] = null;
          if (nextBtn) nextBtn.disabled = true;
          this.renderStep();
        }
      });
    }

    if (backToSearchBtn) {
      backToSearchBtn.addEventListener("click", () => this.resetSearch());
    }

    if (searchInput) {
      searchInput.addEventListener("input", (e) => {
        const searchTerm = String(e.target.value || "").toLowerCase();
        document.querySelectorAll(".vf-option-card").forEach((card) => {
          const text = String(card.textContent || "").toLowerCase();
          card.style.display = text.includes(searchTerm) ? "block" : "none";
        });
      });
    }

    for (let i = 1; i <= 6; i++) {
      const circle = document.getElementById(`vfStep${i}`);
      if (circle) circle.addEventListener("click", () => this.goToStep(i));
    }
  }

  goToStep(targetStep) {
    if (targetStep > this.state.currentStep) return;

    if (targetStep < this.state.currentStep) {
      const stepKeys = ["brand", "model", "type", "year", "technology"];
      for (let i = targetStep - 1; i < stepKeys.length; i++) {
        this.state.selection[stepKeys[i]] = null;
      }
    }

    this.state.currentStep = targetStep;
    const nextBtn = document.getElementById("vfBtnNext");
    if (nextBtn) nextBtn.disabled = true;
    this.renderStep();
  }

  resetSearch() {
    this.state.currentStep = 1;
    this.state.selection = {
      brand: null,
      model: null,
      type: null,
      year: null,
      technology: null
    };

    const res = document.getElementById("vfResultsSection");
    const sel = document.getElementById("vfSelectionSection");
    if (res) res.classList.remove("active");
    if (sel) sel.style.display = "block";

    const nextBtn = document.getElementById("vfBtnNext");
    if (nextBtn) nextBtn.disabled = true;

    this.renderStep();
  }

  async addToCart(handle) {
  if (!handle) {
    alert("⚠️ Handle manquant.");
    return;
  }

  try {
    const product = await fetch(`/products/${handle}.js`, { credentials: "same-origin" }).then((r) => r.json());
    const variantId = product.variants?.[0]?.id;
    if (!variantId) throw new Error("Variant introuvable");

    const r = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
    });

    if (!r.ok) throw new Error(`Cart add failed (${r.status})`);

    // Redirection panier
    window.location.href = "/cart";
  } catch (error) {
    console.error("Erreur addToCart:", error);
    alert("❌ Erreur lors de l'ajout au panier");
  }
}


  viewDetails(handle) {
    if (!handle) {
      alert("⚠️ Handle manquant.");
      return;
    }
    window.location.href = `/products/${handle}`;
  }

  // ------------------- PARSING HELPERS -------------------

  toMoney(v) {
    // accepte: 84.9, "84.90", "84,90", "84,90 €", {amount:"84.90"}, {value:"84.90"}
    if (v === null || v === undefined) return null;

    if (typeof v === "number") {
      return Number.isFinite(v) ? v : null;
    }

    if (typeof v === "object") {
      if (v.amount != null) return this.toMoney(v.amount);
      if (v.value != null) return this.toMoney(v.value);
      return null;
    }

    const s = String(v).trim();
    if (!s) return null;

    // enlève tout sauf chiffres, . , -
    const cleaned = s.replace(/[^\d.,-]/g, "").replace(",", ".");
    const n = Number.parseFloat(cleaned);
    return Number.isFinite(n) ? n : null;
  }

  toCents(v) {
    // product.js: souvent integer (cents) ou string/int
    if (v === null || v === undefined || v === "") return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }

  escapeHtml(text) {
    if (text === null || text === undefined) return "";
    const map = { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#039;" };
    return String(text).replace(/[&<>"']/g, (m) => map[m]);
  }
}

// Initialisation
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => new VehicleFinder());
} else {
  new VehicleFinder();
}
